import React, { useState } from "react";
import {
  canUseNativeShare,
  copyTextToClipboard,
  getRegistrationInviteShareText,
  getRegistrationInviteUrl,
} from "../utils/inviteLink";

const MONO = "'JetBrains Mono', ui-monospace, Menlo, monospace";
const SANS = "'Space Grotesk', -apple-system, sans-serif";
const INK = "#16181A";
const INK_SOFT = "#8E8E88";
const RULE = "#E6E6E1";
const RULE_SOFT = "#F2F2EE";
const PANEL = "#FAFAF8";
const LINK = "#3E8FCC";

/**
 * Shown under the transfer recipient field when the address has no
 * Stabletrust Pay account yet. Offers a normal registration share link
 * (not an invite code / invite-only gate).
 */
export default function RecipientInviteNote({ compact = false, onNotify }) {
  const [copied, setCopied] = useState(false);
  const inviteUrl = getRegistrationInviteUrl();
  const inviteText = getRegistrationInviteShareText(inviteUrl);
  const nativeShare = canUseNativeShare();

  const notify = (message, type = "success") => {
    if (typeof onNotify === "function") {
      onNotify(message, type);
    }
  };

  const handleCopyLink = async () => {
    try {
      await copyTextToClipboard(inviteUrl);
      setCopied(true);
      notify("Invite link copied");
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      notify("Could not copy link", "error");
    }
  };

  const handleShare = async () => {
    try {
      if (nativeShare) {
        try {
          // Prefer text-only — the message already includes the URL.
          // Passing both text and url often duplicates the link.
          await navigator.share({
            title: "Join Stabletrust Pay",
            text: inviteText,
          });
          return;
        } catch (err) {
          if (err && err.name === "AbortError") return;
          // Fall through to clipboard on unexpected share failures.
        }
      }
      await copyTextToClipboard(inviteText);
      setCopied(true);
      notify("Invite message copied");
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      notify("Could not share invite", "error");
    }
  };

  const btnBase = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    border: `1px solid ${INK}`,
    padding: compact ? "12px 16px" : "10px 18px",
    minHeight: compact ? 48 : 42,
    borderRadius: 999,
    fontFamily: SANS,
    fontSize: compact ? 14 : 13.5,
    fontWeight: 500,
    letterSpacing: "0.01em",
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "background .15s, color .15s, border-color .15s",
  };

  const primaryBtn = {
    ...btnBase,
    flex: compact ? "1 1 100%" : undefined,
    background: INK,
    color: "#FFFFFF",
  };

  const secondaryBtn = {
    ...btnBase,
    flex: compact ? 1 : undefined,
    border: `1px solid ${RULE}`,
    background: "#FFFFFF",
    color: INK,
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: compact ? 14 : 12,
        padding: compact ? "16px 16px 14px" : "16px 18px 14px",
        background: PANEL,
        border: `1px solid ${RULE}`,
        color: INK,
        letterSpacing: "normal",
        textTransform: "none",
      }}
    >
      <div>
        <div
          style={{
            fontFamily: SANS,
            fontSize: compact ? 15 : 14.5,
            fontWeight: 600,
            color: INK,
            lineHeight: 1.35,
            letterSpacing: "-0.01em",
          }}
        >
          No confidential account yet
        </div>
        <div
          style={{
            marginTop: 4,
            fontFamily: SANS,
            fontSize: compact ? 13.5 : 13,
            fontWeight: 400,
            color: INK_SOFT,
            lineHeight: 1.5,
            letterSpacing: "0.01em",
          }}
        >
          {nativeShare
            ? "Share this invite so they can create their confidential account, then try again."
            : "Send this invite so they can create their confidential account, then try again."}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: compact ? "12px 14px" : "10px 12px",
          background: "#FFFFFF",
          border: `1px solid ${RULE_SOFT}`,
        }}
      >
        <div
          style={{
            flex: 1,
            minWidth: 0,
            fontFamily: MONO,
            fontSize: compact ? 11.5 : 11,
            color: LINK,
            lineHeight: 1.45,
            letterSpacing: "0.01em",
            wordBreak: "break-all",
            userSelect: "text",
            WebkitUserSelect: "text",
          }}
        >
          {inviteUrl}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 8,
        }}
      >
        {nativeShare ? (
          <button type="button" onClick={handleShare} style={primaryBtn}>
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
            Share
          </button>
        ) : null}
        <button type="button" onClick={handleCopyLink} style={secondaryBtn}>
          {copied ? (
            <>
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Copied
            </>
          ) : (
            <>
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              Copy link
            </>
          )}
        </button>
      </div>
    </div>
  );
}
